import { AiGeneratedLabel, Text } from '@/ui/design-system/components';
import { Plus, SquarePlus } from 'lucide-react';
import { useId, useState } from 'react';
import { Button } from '@/ui/design-system/components/ui/button';
import { Textarea } from '@/ui/design-system/components/ui/textarea';

// The trailing "add a widget" cell shown in the report grid while editing. The
// user describes a widget in free text ("skincare overview in the US") and
// submits; the actual build runs in a separate PendingWidgetBuild tile, so this
// panel stays visible and usable — the user can fire several adds in a row while
// earlier ones are still building. onSubmit receives the trimmed request text.
// Colocated with the Reports views.
function ReportAddWidget({ onSubmit }) {
    const [text, setText] = useState('');
    const disclaimerId = useId();

    const submit = () => {
        const prompt = text.trim();
        if (!prompt) return;
        onSubmit(prompt);
        setText('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className="min-h-[160px] flex flex-col gap-2 rounded-xl border border-dashed border-border bg-background-med-em p-4">
            {/* Header row mirrors a widget's breadcrumb header (Figma node
                2034:10844): a SquarePlus icon + xs muted-foreground label, so the
                add tile reads as a sibling of the surrounding widget tiles. */}
            <div className="flex items-center gap-3">
                <SquarePlus
                    size={16}
                    className="shrink-0 text-muted-foreground"
                />
                <Text size="xs" color="muted">
                    Add a new widget
                </Text>
            </div>
            <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={7}
                placeholder="Describe a widget, e.g. skincare overview in the US"
                aria-describedby={disclaimerId}
                className="resize-none"
            />
            <div className="flex items-center justify-between gap-3">
                <AiGeneratedLabel id={disclaimerId} size="sm" />
                <Button
                    type="button"
                    size="sm"
                    className="gap-2"
                    onClick={submit}
                    disabled={!text.trim()}
                >
                    <Plus size={14} />
                    Add
                </Button>
            </div>
        </div>
    );
}

export default ReportAddWidget;
